import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { RefreshCw, Mail, History, Loader2, RotateCcw } from 'lucide-react';
import type { AdminVippsHendelserResponse } from '@pay/shared';
import { api } from '../../api/client';
import { Money } from '../../components/Money';
import { StatusBadge } from '../../components/StatusBadge';
import { mobilLand, visMobil } from '../../lib/telefon';

/** Navnene i Vipps' hendelseslogg (GET …/payments/{reference}/events), forklart. */
const VIPPS_HENDELSE: Record<string, string> = {
  CREATED: 'Opprettet',
  AUTHORIZED: 'Godkjent av kunden (reservert)',
  CAPTURED: 'Trukket',
  CANCELLED: 'Reservasjonen frigitt',
  REFUNDED: 'Refundert',
  ABORTED: 'Avbrutt av kunden',
  EXPIRED: 'Utløpt',
  TERMINATED: 'Avsluttet av selger',
};

function feilmelding(e: unknown, standard: string): string {
  return (e as { message?: string }).message || standard;
}

interface Detail {
  id: string;
  owner: { id: string; name: string };
  external_reference: string;
  flow: string;
  status: string;
  customer: { name: string; phone: string | null; email: string };
  amount_ore: number;
  description: string;
  metadata: Record<string, unknown> | null;
  callback_url: string | null;
  created_at: string;
  expires_at: string;
  paid_at: string | null;
  refunded_amount_ore: number;
  /** Ført tilbake i ELSE av kallesystemet (ElseWeb kvitterer med …/ack-refund). */
  refund_acknowledged_ore: number;
  events: Array<{ event: string; detail: unknown; occurred_at: string }>;
  attempts: Array<{ id: string; vipps_reference: string; vipps_state: string | null; created_at: string }>;
  webhooks: Array<{
    id: string;
    url: string;
    event: string;
    status: string;
    attempts: number;
    last_status: number | null;
    last_error: string | null;
    created_at: string;
  }>;
}

export default function RequestDetail() {
  const { id } = useParams<{ id: string }>();
  const [d, setD] = useState<Detail | null>(null);
  const [melding, setMelding] = useState<string | null>(null);
  const [vippsLogg, setVippsLogg] = useState<AdminVippsHendelserResponse | null>(null);
  const [henterLogg, setHenterLogg] = useState(false);
  const [loggFeil, setLoggFeil] = useState<string | null>(null);

  function refresh() {
    if (!id) return;
    api.get<Detail>(`/api/admin/requests/${id}`).then(setD);
  }
  useEffect(refresh, [id]);

  // Hentes når noen ber om det: hvert forsøk er et kall til Vipps.
  async function hentVippsLogg() {
    if (!id) return;
    setHenterLogg(true);
    setLoggFeil(null);
    try {
      setVippsLogg(await api.get<AdminVippsHendelserResponse>(`/api/admin/requests/${id}/vipps-events`));
    } catch (e) {
      setLoggFeil(feilmelding(e, 'Kunne ikke hente loggen fra Vipps'));
    } finally {
      setHenterLogg(false);
    }
  }

  function refundert(tekst: string) {
    setMelding(tekst);
    refresh();
    if (vippsLogg) void hentVippsLogg();
  }

  async function syncNow() {
    if (!id) return;
    await api.post(`/api/admin/requests/${id}/sync`);
    refresh();
  }
  async function resend() {
    if (!id) return;
    await api.post(`/api/admin/requests/${id}/resend-receipt`);
    alert('Kvittering lagt i kø');
  }

  if (!d) return <div className="p-6">Laster …</div>;

  return (
    <div className="p-6 max-w-5xl space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm text-slate-500">{d.owner.name}</div>
          <h1 className="text-2xl font-semibold">{d.external_reference}</h1>
          <div className="mt-1 flex items-center gap-3">
            <StatusBadge status={d.status} />
            <span className="text-slate-500 text-sm">{d.flow}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={syncNow} className="flex items-center gap-2 bg-white border border-slate-300 rounded px-3 py-2 text-sm hover:bg-slate-50">
            <RefreshCw size={14} /> Synk
          </button>
          <button onClick={resend} className="flex items-center gap-2 bg-white border border-slate-300 rounded px-3 py-2 text-sm hover:bg-slate-50">
            <Mail size={14} /> Send kvittering på nytt
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-5">
        <Card title="Krav">
          <Row k="Beløp"><Money ore={d.amount_ore} /></Row>
          <Row k="Beskrivelse">{d.description}</Row>
          <Row k="Opprettet">{new Date(d.created_at).toLocaleString('nb-NO')}</Row>
          <Row k="Utløper">{new Date(d.expires_at).toLocaleString('nb-NO')}</Row>
          {d.paid_at && <Row k="Betalt">{new Date(d.paid_at).toLocaleString('nb-NO')}</Row>}
          {d.refunded_amount_ore > 0 && <Row k="Refundert"><Money ore={d.refunded_amount_ore} /></Row>}
          {d.refunded_amount_ore > 0 && (
            <Row k="Ført tilbake i ELSE">
              {d.refund_acknowledged_ore >= d.refunded_amount_ore ? (
                <Money ore={d.refund_acknowledged_ore} />
              ) : (
                <span className="text-amber-800">
                  <Money ore={d.refund_acknowledged_ore} /> — venter på ElseWeb
                </span>
              )}
            </Row>
          )}
        </Card>
        <Card title="Kunde">
          <Row k="Navn">{d.customer.name}</Row>
          <Row k="E-post">{d.customer.email}</Row>
          {d.customer.phone && (
            <Row k="Mobil">
              {visMobil(d.customer.phone)}
              {mobilLand(d.customer.phone) ? ` (${mobilLand(d.customer.phone)})` : ''}
            </Row>
          )}
        </Card>
      </div>

      {melding && (
        <div className="rounded border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">{melding}</div>
      )}

      {(d.status === 'CAPTURED' || d.status === 'PARTIALLY_REFUNDED') && id && (
        <RefusjonKort
          key={d.refunded_amount_ore}
          id={id}
          betaltOre={d.amount_ore}
          refundertOre={d.refunded_amount_ore}
          onRefundert={refundert}
        />
      )}

      <Card title="Hendelser">
        <ul className="text-sm space-y-1">
          {d.events.map((e, i) => (
            <li key={i} className="flex gap-3">
              <span className="text-slate-500 w-44 tabular-nums">{new Date(e.occurred_at).toLocaleString('nb-NO')}</span>
              <span className="font-mono">{e.event}</span>
            </li>
          ))}
        </ul>
      </Card>

      <Card title="Vipps-forsøk">
        <table className="w-full text-sm">
          <thead className="text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left py-1">Tidspunkt</th>
              <th className="text-left py-1">Referanse</th>
              <th className="text-left py-1">State</th>
            </tr>
          </thead>
          <tbody>
            {d.attempts.map((a) => (
              <tr key={a.id} className="border-t border-slate-100">
                <td className="py-1">{new Date(a.created_at).toLocaleString('nb-NO')}</td>
                <td className="py-1 font-mono text-xs">{a.vipps_reference}</td>
                <td className="py-1">{a.vipps_state ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card title="Vipps' hendelseslogg">
        {!vippsLogg ? (
          <div className="flex items-center gap-3">
            <button
              onClick={hentVippsLogg}
              disabled={henterLogg}
              className="flex items-center gap-2 bg-white border border-slate-300 rounded px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
            >
              {henterLogg ? <Loader2 size={14} className="animate-spin" /> : <History size={14} />} Hent fra Vipps
            </button>
            <span className="text-sm text-slate-600">Det Vipps selv har registrert på hvert forsøk: opprettet, godkjent, trukket, refundert …</span>
          </div>
        ) : (
          <div className="space-y-4">
            {vippsLogg.attempts.length === 0 && <div className="text-sm text-slate-600">Kravet har ingen forsøk hos Vipps.</div>}
            {vippsLogg.attempts.map((a) => (
              <div key={a.reference}>
                <div className="text-xs text-slate-600 mb-1">
                  <span className="font-mono">{a.reference}</span> · opprettet {new Date(a.created_at).toLocaleString('nb-NO')}
                </div>
                {a.error ? (
                  <div className="text-sm text-red-700">{a.error}</div>
                ) : a.events.length === 0 ? (
                  <div className="text-sm text-slate-600">Ingen hendelser hos Vipps.</div>
                ) : (
                  <table className="w-full text-sm table-fixed">
                    <colgroup>
                      <col className="w-44" />
                      <col />
                      <col className="w-32" />
                      <col className="w-24" />
                      <col className="w-56" />
                    </colgroup>
                    <thead className="text-slate-600 text-xs uppercase">
                      <tr>
                        <th className="text-left py-1">Tidspunkt</th>
                        <th className="text-left py-1">Hendelse</th>
                        <th className="text-right py-1">Beløp</th>
                        <th className="text-left py-1 pl-4">Resultat</th>
                        <th className="text-left py-1">Idempotency-Key</th>
                      </tr>
                    </thead>
                    <tbody>
                      {a.events.map((e, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="py-1 tabular-nums">{new Date(e.timestamp).toLocaleString('nb-NO')}</td>
                          <td className="py-1">
                            {VIPPS_HENDELSE[e.name] ?? e.name} <span className="font-mono text-xs text-slate-500">{e.name}</span>
                          </td>
                          <td className="py-1 text-right tabular-nums"><Money ore={e.amount_ore} /></td>
                          <td className="py-1 pl-4">{e.success ? 'OK' : <span className="text-red-700">Feilet</span>}</td>
                          <td className="py-1 font-mono text-xs text-slate-600 break-all">{e.idempotency_key ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
            <button
              onClick={hentVippsLogg}
              disabled={henterLogg}
              className="flex items-center gap-2 bg-white border border-slate-300 rounded px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
            >
              {henterLogg ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Oppdater
            </button>
          </div>
        )}
        {loggFeil && <div className="mt-2 text-sm text-red-700">{loggFeil}</div>}
      </Card>

      <Card title="Webhook-leveranser">
        {d.webhooks.length === 0 ? (
          <div className="text-slate-500 text-sm">Ingen webhooks sendt.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-slate-600 text-xs uppercase">
              <tr>
                <th className="text-left py-1">Tidspunkt</th>
                <th className="text-left py-1">URL</th>
                <th className="text-left py-1">Event</th>
                <th className="text-left py-1">Status</th>
                <th className="text-left py-1">Forsøk</th>
                <th className="text-left py-1">HTTP</th>
              </tr>
            </thead>
            <tbody>
              {d.webhooks.map((w) => (
                <tr key={w.id} className="border-t border-slate-100">
                  <td className="py-1 text-slate-600">{new Date(w.created_at).toLocaleString('nb-NO')}</td>
                  <td className="py-1 truncate max-w-xs">{w.url}</td>
                  <td className="py-1 font-mono text-xs">{w.event}</td>
                  <td className="py-1">{w.status}</td>
                  <td className="py-1">{w.attempts}</td>
                  <td className="py-1">{w.last_status ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {d.metadata && (
        <Card title="Metadata">
          <pre className="text-xs bg-slate-50 p-3 rounded overflow-auto">{JSON.stringify(d.metadata, null, 2)}</pre>
        </Card>
      )}
    </div>
  );
}

/** 145550 → «1455,50» (feltverdi, uten tusenskille). */
function somKr(ore: number): string {
  return (ore / 100).toFixed(2).replace('.', ',');
}

/** «1 455,50» / «1455.5» → 145550 øre; null om teksten ikke er et beløp med høyst to desimaler. */
function tilOre(tekst: string): number | null {
  const t = tekst.replace(/[\s ]/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(Number(t) * 100);
}

// Refusjon i vårt eget system (Vipps' sjekkliste): pengene går tilbake til kunden via Vipps, og kravet
// blir Refundert / Delvis refundert. ElseWeb fører den tilbake i ELSE (GET /api/v1/pending-refunds).
function RefusjonKort({
  id,
  betaltOre,
  refundertOre,
  onRefundert,
}: {
  id: string;
  betaltOre: number;
  refundertOre: number;
  onRefundert: (melding: string) => void;
}) {
  const igjen = betaltOre - refundertOre;
  const [belop, setBelop] = useState(() => somKr(igjen));
  const [bekreft, setBekreft] = useState<number | null>(null);
  const [sender, setSender] = useState(false);
  const [feil, setFeil] = useState<string | null>(null);

  function sjekk() {
    const ore = tilOre(belop);
    if (ore === null || ore <= 0) return setFeil('Skriv et beløp større enn 0, f.eks. 250,00.');
    if (ore > igjen) return setFeil(`Høyst ${somKr(igjen)} kr kan refunderes.`);
    setFeil(null);
    setBekreft(ore);
  }

  async function refunder() {
    if (bekreft === null) return;
    setSender(true);
    setFeil(null);
    try {
      await api.post(`/api/admin/requests/${id}/refund`, { amount_ore: bekreft });
      onRefundert(`Refundert ${somKr(bekreft)} kr til kunden via Vipps. ElseWeb fører den tilbake i ELSE.`);
    } catch (e) {
      setFeil(feilmelding(e, 'Refusjonen feilet'));
      setBekreft(null);
    } finally {
      setSender(false);
    }
  }

  return (
    <Card title="Refusjon">
      <div className="flex gap-6 text-sm mb-3">
        <span><span className="text-slate-600">Betalt</span> <Money ore={betaltOre} /></span>
        <span><span className="text-slate-600">Refundert</span> <Money ore={refundertOre} /></span>
        <span><span className="text-slate-600">Kan refunderes</span> <Money ore={igjen} /></span>
      </div>
      {bekreft === null ? (
        <div className="flex items-end gap-3">
          <label className="block">
            <span className="block text-sm text-slate-700 mb-1">Beløp (kr)</span>
            <input
              value={belop}
              onChange={(e) => setBelop(e.target.value)}
              inputMode="decimal"
              className="w-40 border border-slate-300 rounded px-3 py-2 text-right tabular-nums"
            />
          </label>
          <button
            onClick={sjekk}
            className="flex items-center gap-2 bg-white border border-slate-300 rounded px-3 py-2 text-sm hover:bg-slate-50"
          >
            <RotateCcw size={14} /> Refunder …
          </button>
        </div>
      ) : (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm space-y-3">
          <div>
            Refunder <strong>{somKr(bekreft)} kr</strong> til kunden via Vipps? Dette kan ikke angres.
          </div>
          <div className="flex gap-2">
            <button
              onClick={refunder}
              disabled={sender}
              className="flex items-center gap-2 rounded px-3 py-2 text-sm font-medium text-white bg-red-700 hover:bg-red-800 disabled:opacity-50"
            >
              {sender ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Ja, refunder
            </button>
            <button
              onClick={() => setBekreft(null)}
              disabled={sender}
              className="bg-white border border-slate-300 rounded px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
            >
              Avbryt
            </button>
          </div>
        </div>
      )}
      {feil && <div className="mt-2 text-sm text-red-700">{feil}</div>}
      <p className="mt-3 text-xs text-slate-600">
        ElseWeb fører tilbakebetalingen i ELSE av seg selv innen et minutt («Ført tilbake i ELSE» over). Er
        forhåndsbetalingen alt brukt på en faktura, må den føres for hånd.
      </p>
    </Card>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white rounded-lg shadow border border-slate-200 p-5">
      <h2 className="font-semibold mb-3">{title}</h2>
      {children}
    </section>
  );
}

function Row({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between py-1 text-sm">
      <span className="text-slate-600">{k}</span>
      <span>{children}</span>
    </div>
  );
}
